import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Checkbox } from "@progress/kendo-react-inputs";

import {
    FormDropDownList,
    FormAutoComplete,
} from "../../forms/form-components";

import './Report.scss';

export const AdministratorMyHiddenEvents = () => {
    const dispatch = useDispatch();

    const formReference = useRef(null);

    const defaultTicketingWebsite = {
        ticketingWebsiteId: "",
        name: "All Websites",
    };
    const defaultVenueEventStatusType = {
        venueEventStatusTypeId: "",
        name: "All Statuses",
    };
    const defaultReport = {
        reportId: "",
        name: "All Reports",
    };

    const [hiddenEvents, setHiddenEvents] = useState(undefined);
    const [hiddenEventsCount, setHiddenEventsCount] = useState(0);
    const [skipCount, setSkipCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [ticketingWebsites, setTicketingWebsites] = useState([]);
    const [venueEventStatusTypes, setVenueEventStatusTypes] = useState([]);
    const [searchCriteria, setSearchCriteria] = useState(undefined);
    const [venues, setVenues] = useState([]);
    const [venueName, setVenueName] = useState(undefined);
    const [areVenuesLoading, setAreVenuesLoading] = useState(false);
    const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
    const [lastSelectedVenueEventHiddenFromReportKey, setLastSelectedVenueEventHiddenFromReportKey] = useState(undefined);
    const [reports, setReports] = useState([]);

    const autocompleteTimeout = useRef();
    const timeoutDelay = 500; //Milliseconds

    const SelectedCell = (props) => {
        const venueEventHiddenFromReportKey = props.dataItem.venueEventHiddenFromReportKey;
        const selected = props.dataItem.selected;

        return (
            <td className="text-center">
                <Checkbox onChange={(event) => selectedValueChanged(event, venueEventHiddenFromReportKey, !selected)} value={selected} />
            </td>
        );
    };

    const SelectedHeaderCell = () => {
        return (
            <div className="text-center">
                <Checkbox onChange={headerCheckboxValueChanged} value={isHeaderCheckboxChecked} />
            </div>
        );
    };

    const ViewUrlHeaderCell = () => {
        return (
            <div>
                <Button type={"button"} fillMode="link" title="View Selected Websites" onClick={viewSelectedUrls} className="p-0">
                    View Selected
                </Button>
            </div>
        );
    };

    const ViewUrlCell = (props) => {
        const url = props.dataItem[props.field];

        return (
            <td className={"text-nowrap"}>
                {url ? <Link to={{ pathname: url }} target={"_blank"} rel={"noopener noreferrer"}>View Website</Link> : ""}
            </td>
        );
    };

    const viewSelectedUrls = () => {
        if (hiddenEvents.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No websites to view");
            return;
        }

        let count = 0;

        hiddenEvents.forEach(dataItem => {
            if (dataItem.selected === true) {
                count++;
                window.open(dataItem.ticketingUrl, "_blank", "noreferrer");
            }
        });

        if (count === 0) {
            addNotificationWithTimeout(dispatch, "error", "No websites to view");
            return;
        }
    };

    const getTicketingWebsites = () => {
        const dataItem = {
            onlyActive: true,
        }

        fetch(`/api/general/ticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                const allTicketingWebsites = [defaultTicketingWebsite].concat(result);
                setTicketingWebsites(allTicketingWebsites);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getVenueEventStatusTypes = () => {
        fetch(`/api/general/venueeventstatustypes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allVenueEventStatusTypes = [defaultVenueEventStatusType].concat(result);
                setVenueEventStatusTypes(allVenueEventStatusTypes);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const venueChanged = (event) => {
        const value = event.value;

        clearTimeout(autocompleteTimeout.current);

        if (!value || value.length < 2) {
            setVenues([]);
            setAreVenuesLoading(false);
            setVenueName(null);
            return;
        }

        autocompleteTimeout.current = setTimeout(() => {
            const dataItem = {
                name: value,
                onlyActive: true,
            };

            fetch(`/api/administrator/venues`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        "X-XSRF-TOKEN": getCSRFToken(),
                    },
                    body: JSON.stringify(dataItem),
                })
                .then(handleResponse)
                .then(result => {
                    setVenues(result.venues);
                    setVenueName(value);
                })
                .catch(error => {
                    addNotificationWithTimeout(dispatch, "error", error.message);
                })
                .finally(() => {
                    setAreVenuesLoading(false);
                });
        }, timeoutDelay);

        setAreVenuesLoading(true);
    };

    const getHiddenEvents = (dataItem, newPageNumber) => {
        setIsLoading(true);

        if (!dataItem) {
            dataItem = searchCriteria;
        }

        if (!dataItem) {
            dataItem = {};
        }

        dataItem.pageNumber = newPageNumber;

        fetch(`/api/administrator/report/myhiddenevents`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setItemsPerPage(result.itemsPerPage);
                setSkipCount(result.itemsPerPage * (result.pageNumber - 1));
                setHiddenEventsCount(result.totalCount);

                let hiddenEventsCopy = result.hiddenEvents;

                hiddenEventsCopy.forEach(dataItem => {
                    dataItem.selected = false;
                });

                setHiddenEvents(hiddenEventsCopy);
                setIsHeaderCheckboxChecked(false);
                setLastSelectedVenueEventHiddenFromReportKey(undefined);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const stopHidingEvents = (venueEventHiddenFromReportKeys) => {
        if (window.confirm("Are you sure you want to stop hiding the selected events?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            venueEventHiddenFromReportKeys: venueEventHiddenFromReportKeys,
        };

        fetch("/api/administrator/venueeventhiddenfromreport",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Stopped hiding the selected events");

                getHiddenEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const stopHidingSelectedEvents = () => {
        let venueEventHiddenFromReportKeys = [];

        hiddenEvents.forEach(dataItem => {
            if (dataItem.selected === true) {
                venueEventHiddenFromReportKeys.push(dataItem.venueEventHiddenFromReportKey);
            }
        });

        if (venueEventHiddenFromReportKeys.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No events selected");
            return;
        }

        stopHidingEvents(venueEventHiddenFromReportKeys);
    };

    const getData = (dataItem) => {
        if (dataItem.report !== undefined
            && dataItem.report.value !== "") {
            dataItem.reportId = parseInt(dataItem.report.reportId);
        }
        else {
            dataItem.reportId = undefined;
        }

        if (dataItem.ticketingWebsite !== undefined
            && dataItem.ticketingWebsite.value !== "") {
            dataItem.ticketingWebsiteId = parseInt(dataItem.ticketingWebsite.ticketingWebsiteId);
        }
        else {
            dataItem.ticketingWebsiteId = undefined;
        }

        if (dataItem.venueEventStatusType !== undefined
            && dataItem.venueEventStatusType.value !== "") {
            dataItem.venueEventStatusTypeId = parseInt(dataItem.venueEventStatusType.venueEventStatusTypeId);
        }
        else {
            dataItem.venueEventStatusTypeId = undefined;
        }

        dataItem.venueKey = undefined;
        if (venues.length > 0) {
            const venue = venues.find(element => element.currentName === venueName);

            if (venue) {
                dataItem.venueKey = venue.venueKey;
            }
        }

        setSearchCriteria(dataItem);
        getHiddenEvents(dataItem);
    }

    const pageChange = (event) => {
        const newPageNumber = (event.page.skip / event.page.take) + 1;
        getHiddenEvents(null, newPageNumber);
    };

    const selectedValueChanged = (event, venueEventHiddenFromReportKey, selected) => {
        setIsLoading(true);

        setTimeout(() => {
            let hiddenEventsCopy = hiddenEvents;

            const itemIndex = hiddenEventsCopy.findIndex(dataItem =>
                dataItem.venueEventHiddenFromReportKey === venueEventHiddenFromReportKey
            );

            let isSelectingMultipleItems = false;

            //Shift selection of multiple items            
            if (event.nativeEvent.shiftKey
                && lastSelectedVenueEventHiddenFromReportKey !== undefined
                && lastSelectedVenueEventHiddenFromReportKey !== venueEventHiddenFromReportKey) {
                const lastSelectedItemIndex = hiddenEventsCopy.findIndex(dataItem =>
                    dataItem.venueEventHiddenFromReportKey === lastSelectedVenueEventHiddenFromReportKey
                );

                if (lastSelectedItemIndex >= 0) {
                    isSelectingMultipleItems = true;

                    let lowIndex = -1;
                    let highIndex = -1;

                    if (itemIndex > lastSelectedItemIndex) {
                        lowIndex = lastSelectedItemIndex;
                        highIndex = itemIndex;
                    }
                    else {
                        lowIndex = itemIndex;
                        highIndex = lastSelectedItemIndex;
                    }

                    for (let i = lowIndex; i <= highIndex; i++) {
                        hiddenEventsCopy[i].selected = selected;
                    }
                }
            }

            if (isSelectingMultipleItems === false) {
                hiddenEventsCopy[itemIndex].selected = selected;
            }

            if (selected === true) {
                setLastSelectedVenueEventHiddenFromReportKey(venueEventHiddenFromReportKey);
            }

            setHiddenEvents(hiddenEventsCopy);

            setIsHeaderCheckboxChecked(false);
            setIsLoading(false);
        }, 1);
    };

    const headerCheckboxValueChanged = () => {
        setIsLoading(true);

        setTimeout(() => {
            const checked = !isHeaderCheckboxChecked;
            setIsHeaderCheckboxChecked(checked);

            let hiddenEventsCopy = hiddenEvents;

            hiddenEventsCopy.forEach(dataItem =>
                dataItem.selected = checked
            );

            setHiddenEvents(hiddenEventsCopy);

            setLastSelectedVenueEventHiddenFromReportKey(undefined);

            setIsLoading(false);
        }, 1);
    };

    const getReports = () => {
        fetch(`/api/general/reports`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allReports = [defaultReport].concat(result);
                setReports(allReports);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    useEffect(() => {
        document.title = "My Hidden Events";

        getTicketingWebsites();
        getVenueEventStatusTypes();
        getReports();
    }, []);

    return (
        <div className="administratorReport">
            <Form
                onSubmit={getData}
                ignoreModified={true}
                ref={formReference}
                render={(formRenderProps) => (
                    <FormElement>
                        <fieldset className={"k-form-fieldset container"}>
                            <div className="row">
                                <div className="col-md-4">
                                    <Field
                                        id={"report"}
                                        name={"report"}
                                        label={"Report"}
                                        component={FormDropDownList}
                                        data={reports}
                                        textField="name"
                                        dataItemKey="reportId"
                                        defaultValue={reports.find(element => element.reportId === "")}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Field
                                        id={"ticketingWebsite"}
                                        name={"ticketingWebsite"}
                                        label={"Ticketing website"}
                                        component={FormDropDownList}
                                        data={ticketingWebsites}
                                        textField="name"
                                        dataItemKey="ticketingWebsiteId"
                                        defaultValue={ticketingWebsites.find(element => element.ticketingWebsiteId === "")}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Field
                                        id={"venueEventStatusType"}
                                        name={"venueEventStatusType"}
                                        label={"Event status"}
                                        component={FormDropDownList}
                                        data={venueEventStatusTypes}
                                        textField="name"
                                        dataItemKey="venueEventStatusTypeId"
                                        defaultValue={venueEventStatusTypes.find(element => element.venueEventStatusTypeId === "")}
                                    />
                                </div>
                                <div className="col-md-8">
                                    <Field
                                        id={"venueName"}
                                        name={"venueName"}
                                        label={"Venue"}
                                        component={FormAutoComplete}
                                        data={venues}
                                        placeholder="e.g. Roseland"
                                        textField="currentName"
                                        onChange={venueChanged}
                                        loading={areVenuesLoading}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <div className="k-form-field">
                                        <label className="k-label">&nbsp;</label>
                                        <Button
                                            type={"submit"}
                                            themeColor={"primary"}
                                            icon="search"
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </FormElement>
                )}
            />

            {isLoading ? <LoadingPanel /> : <></>}

            {!hiddenEvents ? <></> :
                <>
                    <div className="mb-3 ms-2 d-flex flex-row">
                        <Button type={"button"} icon="plus" title="Stop Hiding Selected Events" onClick={stopHidingSelectedEvents}>
                            Stop Hiding Selected Events
                        </Button>
                    </div>

                    <Grid
                        data={hiddenEvents}
                        resizable={true}
                        skip={skipCount}
                        take={itemsPerPage}
                        total={hiddenEventsCount}
                        pageable={true}
                        onPageChange={pageChange}
                        scrollable="none"
                        dataItemKey="venueEventHiddenFromReportKey"
                    >
                        <Column cell={SelectedCell} headerCell={SelectedHeaderCell} />
                        <Column field="ticketingUrl" cell={ViewUrlCell} headerCell={ViewUrlHeaderCell} />
                        <Column field="reportName" title={"Report"} />
                        <Column field="dateCreated" title={"Date Hidden"} />
                        <Column field="ticketingWebsiteName" title={"Website"} />
                        <Column field="eventDate" title={"Event Date"} />
                        <Column field="eventName" title={"Event Name"} />                        
                        <Column field="venueEventStatusTypeName" title={"Status"} />
                        <Column field="venueName" title={"Venue"} />
                    </Grid>
                </>
            }
        </div>
    );
}